import { Flex } from '@chakra-ui/react';
import { mealPlanMolecule } from '@web-app/atoms/charting/mealPlanMolecule';
import {
  MealPlanAnalyticsContext,
  MealPlanView,
  shouldShowLoadingState,
} from '@web-app/components/CreateMealPlan/utils';
import { Text } from '@web-app/components/Typography/Text';
import { useAutoGenerateInstructions } from '@web-app/hooks/useAutoGenerateInstructions';
import { useMolecule } from 'bunshi/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { type ReactNode, useEffect, useState } from 'react';

import { BerryLoader } from '../BerryLoader';
import { DetailedMealPlan } from './DetailedMealPlan';
import { MealPlanInstructions } from './MealPlanInstructions';
import { MealPlanSummary } from './MealPlanSummary';

export type CreateMealPlanVariant = 'drawer' | 'standalone' | 'note';

interface CreateMealPlanProps {
  variant?: CreateMealPlanVariant;
  context?: MealPlanAnalyticsContext;
  usePreviousAppointment?: boolean;
}

const loadingText = {
  [MealPlanView.Instructions]: 'Loading meal plan instructions...',
  [MealPlanView.Summary]: 'Loading meal plan summary...',
  [MealPlanView.Details]: 'Loading meal plan recipes...',
};

export const CreateMealPlan = ({
  variant = 'standalone',
  context = 'in_session',
  usePreviousAppointment,
}: CreateMealPlanProps) => {
  const {
    activeMealPlanStepAtom,
    mealPlanInstructionsAtom,
    areMealPlanInstructionsLoadingAtom,
    mealPlanSummaryAtom,
    isMealPlanSummaryLoadingAtom,
    detailedMealPlanAtom,
    isDetailedMealPlanLoadingAtom,
    resetMealPlanAtom,
    refetchMealPlanAtom,
    hasSetInitialStepAtom,
    analyticsContextAtom,
    isMealPlanSentAtom,
  } = useMolecule(mealPlanMolecule);

  const { isGeneratingInstructions } = useAutoGenerateInstructions({
    usePreviousAppointment,
  });

  const [viewToShow, setActiveMealPlanStep] = useAtom(activeMealPlanStepAtom);
  const mealPlanInstructions = useAtomValue(mealPlanInstructionsAtom);
  const areMealPlanInstructionsLoading = useAtomValue(
    areMealPlanInstructionsLoadingAtom,
  );
  const mealPlanSummary = useAtomValue(mealPlanSummaryAtom);
  const isMealPlanSummaryLoading = useAtomValue(isMealPlanSummaryLoadingAtom);
  const detailedMealPlan = useAtomValue(detailedMealPlanAtom);
  const isDetailedMealPlanLoading = useAtomValue(isDetailedMealPlanLoadingAtom);
  const resetMealPlanState = useSetAtom(resetMealPlanAtom);
  const refetchMealPlan = useSetAtom(refetchMealPlanAtom);
  const isMealPlanSent = useAtomValue(isMealPlanSentAtom);
  const [hasSetInitialStep, setHasSetInitialStep] = useAtom(
    hasSetInitialStepAtom,
  );
  const setAnalyticsContext = useSetAtom(analyticsContextAtom);

  // When the broadcast step is ahead of what the query cache has, or the
  // broadcast says "sent" but the cache doesn't reflect it, the cache is stale
  // (meal plan was advanced/sent in a standalone tab). Refetch so the
  // initial-step logic and read-only state see fresh data.
  const isCacheStale =
    (viewToShow === MealPlanView.Details && !detailedMealPlan) ||
    (viewToShow === MealPlanView.Summary && !mealPlanSummary) ||
    (isMealPlanSent && !detailedMealPlan?.isSent);
  const [isRefetchingStaleCache, setIsRefetchingStaleCache] =
    useState(isCacheStale);

  useEffect(
    function refetchStaleCrossTabCache() {
      if (!isCacheStale) return;
      setIsRefetchingStaleCache(true);
      refetchMealPlan().finally(() => setIsRefetchingStaleCache(false));
    },
    [isCacheStale, refetchMealPlan],
  );

  const readOnly = !!detailedMealPlan?.isSent;

  // Set analytics context so child components can include it in events
  useEffect(
    function syncAnalyticsContext() {
      setAnalyticsContext(context);
    },
    [context, setAnalyticsContext],
  );

  // Determine initial step based on available data (jumps to farthest progress point).
  // hasSetInitialStep lives in the molecule so it persists across Chakra tab switches
  // within the note wizard, preventing this effect from re-running while the user
  // navigates between steps. It resets on unmount (resetMealPlanState) so reopening
  // the meal plan tab correctly jumps to the farthest step again.
  useEffect(
    function setInitialMealPlanStep() {
      if (
        hasSetInitialStep ||
        areMealPlanInstructionsLoading ||
        isDetailedMealPlanLoading ||
        isMealPlanSummaryLoading ||
        isRefetchingStaleCache
      ) {
        return;
      }

      if (detailedMealPlan) {
        setActiveMealPlanStep(MealPlanView.Details);
      } else if (mealPlanSummary) {
        setActiveMealPlanStep(MealPlanView.Summary);
      }

      setHasSetInitialStep(true);
    },
    [
      hasSetInitialStep,
      setHasSetInitialStep,
      detailedMealPlan,
      mealPlanSummary,
      areMealPlanInstructionsLoading,
      isDetailedMealPlanLoading,
      isMealPlanSummaryLoading,
      isRefetchingStaleCache,
      setActiveMealPlanStep,
    ],
  );

  useEffect(
    function resetMealPlanStateOnUnmount() {
      return () => {
        resetMealPlanState();
      };
    },
    [resetMealPlanState],
  );

  const loadingState = (
    <Flex
      width="100%"
      flex={1}
      minHeight="300px"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
    >
      <BerryLoader text={<Text>{loadingText[viewToShow]}</Text>} />
    </Flex>
  );

  const errorState = (
    <Flex
      width="100%"
      flex={1}
      minHeight="300px"
      justifyContent="center"
      alignItems="center"
      flexDirection="column"
    >
      <Text>Something went wrong. Please refresh and try again.</Text>
    </Flex>
  );

  const shouldShowLoader = shouldShowLoadingState([
    {
      isPending: areMealPlanInstructionsLoading,
      hasData: !!mealPlanInstructions,
    },
    { isPending: isMealPlanSummaryLoading, hasData: !!mealPlanSummary },
    { isPending: isDetailedMealPlanLoading, hasData: !!detailedMealPlan },
  ]);

  if (shouldShowLoader) {
    return loadingState;
  }

  let content: ReactNode;
  switch (viewToShow) {
    case MealPlanView.Instructions:
      if (areMealPlanInstructionsLoading || isGeneratingInstructions) {
        return loadingState;
      }
      content = <MealPlanInstructions readOnly={readOnly} />;
      break;
    case MealPlanView.Summary:
      if (
        isMealPlanSummaryLoading ||
        !mealPlanSummary ||
        !mealPlanInstructions
      ) {
        return loadingState;
      }
      content = <MealPlanSummary readOnly={readOnly} />;
      break;
    case MealPlanView.Details:
      if (isDetailedMealPlanLoading || !detailedMealPlan || !mealPlanSummary) {
        return loadingState;
      }
      content = <DetailedMealPlan readOnly={readOnly} />;
      break;
    default:
      return errorState;
  }

  return (
    <Flex direction="column" {...(variant === 'drawer' ? { mx: 0 } : {})}>
      {content}
    </Flex>
  );
};
